import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Zap, Edit2, Trash2, Check, Globe, FileText,
    Linkedin, Twitter, Instagram, Facebook, Megaphone, Mail, BarChart2, RefreshCw,
    Share2, CheckCircle, Rocket, RotateCcw, X, ExternalLink, Wifi, WifiOff,
    Package, Plus, Search, Link2, Unlink, Trophy, Layers, Activity, TrendingUp, Eye,
    RotateCw, Send,
} from 'lucide-react';
import { GlassCard, GlassButton, GlassBadge, GlassTabs, GlassTab, EmailChipInput } from '../ui';
import {
    getCampaign, generateCampaign, deleteCampaign, updateCampaign,
    updateAsset, deleteAsset, regenerateSingleAsset, createSequence,
    syncCampaignToCRM, getCampaignCRMStatus,
    getCampaignAnalytics,
    deployCampaign, getCampaignDeployments, rollbackDeployment,
    getCampaignProducts, linkProductToCampaign, unlinkProductFromCampaign,
    getProducts,
    promoteVariant, getGroupedAssets, unlinkVariantGroup,
    enableAutoScale, disableAutoScale, getCampaignHealth, iterateCampaign,
    getUnifiedPerformance,
    fetchCampaignAssetImage, uploadCampaignAssetImage, setCampaignAssetImageUrl,
    generateAdImage,
    publishCampaignAssetNow, getSocialAccounts, getPluginWordPressSites,
    apiClient,
    Campaign, CampaignAsset, DeploymentRecord, Product, OverlayState,
} from '../../services/api';
import TextOverlayModal from '../shared/TextOverlayModal';
import SocialAssetEditor from '../shared/SocialAssetEditor';
import { Type as TextIcon } from 'lucide-react';
import EmailSequenceBuilder from './EmailSequenceBuilder';
import JourneyCanvas from './JourneyCanvas';
import BlogPostEditorModal from './BlogPostEditorModal';
import CampaignGenerationProgressModal from './CampaignGenerationProgressModal';
import { useLandingPagePreview } from '../../hooks/useLandingPagePreview';
import './Campaigns.css';
import '../shared/AssetCardGrid.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


const ASSET_TYPE_META: Record<string, { label: string; icon: React.ReactNode }> = {
    landing_page: { label: 'Landing Page', icon: <Globe size={14} /> },
    blog_post: { label: 'Blog Post', icon: <FileText size={14} /> },
    social_linkedin: { label: 'LinkedIn', icon: <Linkedin size={14} /> },
    social_twitter: { label: 'Twitter / X', icon: <Twitter size={14} /> },
    social_instagram: { label: 'Instagram', icon: <Instagram size={14} /> },
    social_facebook: { label: 'Facebook', icon: <Facebook size={14} /> },
    ad_copy: { label: 'Ad Copy', icon: <Megaphone size={14} /> },
};

const ASSET_STATUS_VARIANT: Record<string, 'success' | 'warning' | 'info' | 'default'> = {
    approved: 'success',
    published: 'info',
    draft: 'default',
    archived: 'warning',
};

const TABS: GlassTab[] = [
    { id: 'Overview', label: 'Overview' },
    { id: 'Assets', label: 'Assets' },
    { id: 'Journey Builder', label: 'Journey Builder' },
    { id: 'Products', label: 'Products' },
    { id: 'Auto-Scale', label: 'Auto-Scale' },
    { id: 'Performance', label: 'Performance' },
];

// ---------------------------------------------------------------------------
// Product Picker Modal
// ---------------------------------------------------------------------------

interface ProductPickerProps {
    onSelect: (product: Product) => void;
    onClose: () => void;
    excludeIds: string[];
}

const ProductPickerModal: React.FC<ProductPickerProps> = ({ onSelect, onClose, excludeIds }) => {
    const [allProducts, setAllProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [linking, setLinking] = useState<string | null>(null);

    useEffect(() => {
        getProducts()
            .then(data => setAllProducts(data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const filtered = allProducts.filter(p =>
        !excludeIds.includes(p.product_id) &&
        (p.name.toLowerCase().includes(search.toLowerCase()) ||
         (p.category || '').toLowerCase().includes(search.toLowerCase()))
    );

    const fmtPrice = (price?: number) =>
        price != null
            ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(price)
            : '—';

    return (
        <div
            style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
            onClick={onClose}
        >
            <div style={{ width: '100%', maxWidth: 520 }} onClick={e => e.stopPropagation()}>
                <GlassCard variant="strong" padding="none" style={{ overflow: 'hidden' }}>
                    {/* Header */}
                    <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--stroke)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <Link2 size={18} style={{ color: 'var(--primary-500)' }} />
                            <span style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text)' }}>Link a Product</span>
                        </div>
                        <GlassButton variant="ghost" size="sm" icon={<X size={16} />} onClick={onClose} />
                    </div>

                    {/* Search */}
                    <div style={{ padding: '14px 24px', borderBottom: '1px solid var(--stroke)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--panel)', border: '1px solid var(--stroke)', borderRadius: 'var(--radius-md)', padding: '8px 12px' }}>
                            <Search size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                            <input
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Search products…"
                                style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 'var(--text-sm)', color: 'var(--text)', width: '100%' }}
                                autoFocus
                            />
                        </div>
                    </div>

                    {/* List */}
                    <div style={{ maxHeight: 340, overflowY: 'auto', padding: '8px 0' }}>
                        {loading && (
                            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
                                <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite', display: 'inline' }} /> Loading…
                            </div>
                        )}
                        {!loading && filtered.length === 0 && (
                            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
                                {allProducts.length === 0 ? 'No products found.' : 'No matching products.'}
                            </div>
                        )}
                        {!loading && filtered.map(p => (
                            <div
                                key={p.product_id}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                                    padding: '10px 24px', cursor: 'pointer',
                                    transition: 'background var(--transition-normal)',
                                }}
                                onMouseEnter={e => (e.currentTarget.style.background = 'var(--panel-hover)')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                            >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 'var(--font-medium)', color: 'var(--text)', fontSize: 'var(--text-sm)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: 2 }}>
                                        {p.category && <span>{p.category} · </span>}
                                        {fmtPrice(p.price)}
                                    </div>
                                </div>
                                <GlassButton
                                    variant="primary"
                                    size="sm"
                                    icon={<Plus size={14} />}
                                    disabled={linking === p.product_id}
                                    loading={linking === p.product_id}
                                    onClick={async () => {
                                        setLinking(p.product_id);
                                        try { await onSelect(p); } finally { setLinking(null); }
                                    }}
                                >
                                    Link
                                </GlassButton>
                            </div>
                        ))}
                    </div>

                    {/* Footer */}
                    <div style={{ padding: '12px 24px', borderTop: '1px solid var(--stroke)', display: 'flex', justifyContent: 'flex-end' }}>
                        <GlassButton variant="secondary" onClick={onClose}>Close</GlassButton>
                    </div>
                </GlassCard>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Campaign Performance Panel (H2.5 real data)
// ---------------------------------------------------------------------------

const CampaignPerformancePanel: React.FC<{ campaignId: string }> = ({ campaignId }) => {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        getCampaignAnalytics(campaignId)
            .then(d => { setData(d); setError(null); })
            .catch(() => setError('Failed to load performance data.'))
            .finally(() => setLoading(false));
    }, [campaignId]);

    if (loading) return (
        <GlassCard style={{ padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                Loading performance data…
            </div>
        </GlassCard>
    );

    if (error) return (
        <GlassCard style={{ padding: '24px' }}>
            <p style={{ color: 'var(--danger)', fontSize: '0.9rem', margin: 0 }}>{error}</p>
        </GlassCard>
    );

    if (!data) return null;

    const assetsByType = data.assets?.by_type || {};
    const assetsByStatus = data.assets?.by_status || {};
    const maxType = Math.max(...Object.values(assetsByType) as number[], 1);
    const maxStatus = Math.max(...Object.values(assetsByStatus) as number[], 1);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Asset counts */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
                {[
                    { label: 'Total Assets', value: data.assets?.total ?? 0 },
                    { label: 'Sequences', value: data.email_sequences?.length ?? 0 },
                    { label: 'CRM Synced', value: data.crm_metrics?.synced ? 'Yes' : 'No' },
                ].map(m => (
                    <GlassCard key={m.label} style={{ padding: '16px', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.6rem', fontWeight: 300, color: 'var(--text)' }}>{m.value}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: 4 }}>{m.label}</div>
                    </GlassCard>
                ))}
            </div>

            {/* Asset breakdowns */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <GlassCard style={{ padding: '16px' }}>
                    <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)', margin: '0 0 12px 0' }}>Assets by Type</h4>
                    {Object.entries(assetsByType).length === 0 ? (
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: 0 }}>No assets yet.</p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {Object.entries(assetsByType).map(([type, count]) => (
                                <div key={type}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <span style={{ fontSize: '0.8rem', color: 'var(--text)', textTransform: 'capitalize' }}>{type.replace(/_/g, ' ')}</span>
                                        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{count as number}</span>
                                    </div>
                                    <div style={{ height: 6, background: 'var(--panel-strong)', borderRadius: 3, overflow: 'hidden' }}>
                                        <div style={{ height: '100%', background: 'var(--primary-500)', width: `${Math.round(((count as number) / maxType) * 100)}%`, borderRadius: 3 }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </GlassCard>

                <GlassCard style={{ padding: '16px' }}>
                    <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)', margin: '0 0 12px 0' }}>Assets by Status</h4>
                    {Object.entries(assetsByStatus).length === 0 ? (
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: 0 }}>No assets yet.</p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {Object.entries(assetsByStatus).map(([status, count]) => (
                                <div key={status}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <span style={{ fontSize: '0.8rem', color: 'var(--text)', textTransform: 'capitalize' }}>{status}</span>
                                        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{count as number}</span>
                                    </div>
                                    <div style={{ height: 6, background: 'var(--panel-strong)', borderRadius: 3, overflow: 'hidden' }}>
                                        <div style={{ height: '100%', background: 'var(--success)', width: `${Math.round(((count as number) / maxStatus) * 100)}%`, borderRadius: 3 }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </GlassCard>
            </div>

            {/* Email sequences */}
            {data.email_sequences?.length > 0 && (
                <GlassCard style={{ padding: '16px' }}>
                    <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)', margin: '0 0 12px 0' }}>Email Sequences</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {data.email_sequences.map((seq: any) => (
                            <div key={seq.sequence_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)' }}>
                                <div>
                                    <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text)' }}>{seq.name}</div>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2 }}>{seq.steps} steps · {seq.trigger_type || 'manual'}</div>
                                </div>
                                <GlassBadge size="sm" variant={seq.synced_to_crm ? 'success' : 'default'}>
                                    {seq.synced_to_crm ? 'CRM Synced' : 'Not synced'}
                                </GlassBadge>
                            </div>
                        ))}
                    </div>
                </GlassCard>
            )}

            {/* CRM status */}
            <GlassCard style={{ padding: '16px' }}>
                <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)', margin: '0 0 8px 0' }}>CRM Sync</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {data.crm_metrics?.synced ? (
                        <>
                            <CheckCircle size={16} style={{ color: 'var(--success)' }} />
                            <span style={{ fontSize: '0.85rem', color: 'var(--text)' }}>
                                Synced to SuiteCRM{data.crm_metrics.last_synced ? ` · ${new Date(data.crm_metrics.last_synced).toLocaleDateString()}` : ''}
                            </span>
                        </>
                    ) : (
                        <>
                            <BarChart2 size={16} style={{ color: 'var(--text-secondary)' }} />
                            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Not yet synced to CRM</span>
                        </>
                    )}
                </div>
            </GlassCard>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Social asset type list — used to gate which assets open the social editor
// ---------------------------------------------------------------------------

const SOCIAL_ASSET_TYPES = ['social_linkedin', 'social_twitter', 'social_instagram', 'social_facebook'];

interface AssetEditorProps {
    asset: CampaignAsset;
    onSave: (content: string, title: string) => Promise<void>;
    onClose: () => void;
    onOpenOverlay: (asset: CampaignAsset) => void;
}

const AssetEditor: React.FC<AssetEditorProps> = ({ asset, onSave, onClose, onOpenOverlay }) => {
    const [content, setContent] = useState(asset.content || '');
    const [title, setTitle] = useState(asset.title || '');
    const [saving, setSaving] = useState(false);
    const assetEditorMouseDownTarget = useRef<EventTarget | null>(null);

    const handleSave = async () => {
        setSaving(true);
        try { await onSave(content, title); onClose(); }
        finally { setSaving(false); }
    };

    return (
        <div
            className="asset-editor-overlay"
            onMouseDown={e => { assetEditorMouseDownTarget.current = e.target; }}
            onClick={e => { if (e.target === e.currentTarget && assetEditorMouseDownTarget.current === e.currentTarget) onClose(); }}
        >
            <div className="asset-editor-modal" onClick={e => e.stopPropagation()}>
                <h3>Edit {ASSET_TYPE_META[asset.asset_type]?.label || asset.asset_type}</h3>
                <input
                    style={{
                        background: 'var(--panel-strong)', border: '1px solid var(--stroke)',
                        borderRadius: 'var(--radius-md)', padding: '8px 12px',
                        color: 'var(--text)', fontSize: '0.9rem', width: '100%', outline: 'none',
                    }}
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    placeholder="Title"
                />
                <textarea
                    value={content}
                    onChange={e => setContent(e.target.value)}
                    placeholder="Content…"
                />
                {asset.image_url && (
                    <GlassButton
                        variant="secondary"
                        onClick={() => onOpenOverlay(asset)}
                        style={{ width: '100%', marginBottom: 8 }}
                    >
                        <TextIcon size={13} /> Add Overlay
                    </GlassButton>
                )}
                <div className="asset-editor-footer">
                    <GlassButton variant="secondary" onClick={onClose}>Cancel</GlassButton>
                    <GlassButton variant="primary" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                    </GlassButton>
                </div>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Deployment Dashboard
// ---------------------------------------------------------------------------

const CHANNEL_ICONS: Record<string, React.ReactNode> = {
    landing_page: <Globe size={16} />,
    email_sequence: <Mail size={16} />,
    lead_workflow: <RefreshCw size={16} />,
    social_linkedin: <Linkedin size={16} />,
    social_twitter: <Twitter size={16} />,
    social_instagram: <Instagram size={16} />,
    social_facebook: <Facebook size={16} />,
    wordpress: <FileText size={16} />,
};

const CHANNEL_LABELS: Record<string, string> = {
    landing_page: 'Landing Page',
    email_sequence: 'Email Sequences',
    lead_workflow: 'Lead Workflows',
    social_linkedin: 'LinkedIn',
    social_twitter: 'Twitter / X',
    social_instagram: 'Instagram',
    social_facebook: 'Facebook',
    wordpress: 'WordPress',
};

function channelStatusVariant(status: string): 'success' | 'warning' | 'danger' | 'info' | 'default' {
    switch (status) {
        case 'live':
        case 'active':
        case 'scheduled': return 'success';
        case 'deploying': return 'warning';
        case 'queued': return 'info';
        case 'failed': return 'danger';
        default: return 'default';
    }
}

function overallStatusLabel(status: string): string {
    switch (status) {
        case 'live': return 'Campaign Live';
        case 'partial': return 'Partially Deployed';
        case 'deploying': return 'Deploying...';
        case 'failed': return 'Deployment Failed';
        case 'rolled_back': return 'Rolled Back';
        default: return status;
    }
}

function overallStatusVariant(status: string): 'success' | 'warning' | 'danger' | 'info' | 'default' {
    switch (status) {
        case 'live': return 'success';
        case 'deploying': return 'warning';
        case 'partial': return 'info';
        case 'failed': return 'danger';
        default: return 'default';
    }
}

interface DeploymentDashboardProps {
    campaignId: string;
    deployment: DeploymentRecord | null;
    previousDeployments: DeploymentRecord[];
    deploying: boolean;
    onRollback: (deploymentId: string) => Promise<void>;
    onClose: () => void;
}

const DeploymentDashboard: React.FC<DeploymentDashboardProps> = ({
    campaignId,
    deployment,
    previousDeployments,
    deploying,
    onRollback,
    onClose,
}) => {
    const [rollingBack, setRollingBack] = useState(false);
    const [rollbackError, setRollbackError] = useState('');

    const canRollback = deployment &&
        deployment.overall_status !== 'rolled_back' &&
        (() => {
            const ms = new Date().getTime() - new Date(deployment.deployed_at).getTime();
            return ms < 60 * 60 * 1000; // 60 minutes
        })();

    const handleRollback = async () => {
        if (!deployment) return;
        if (!window.confirm('Roll back this deployment? Email sequences will return to draft, workflows to paused, and social assets to draft.')) return;
        setRollingBack(true);
        setRollbackError('');
        try {
            await onRollback(deployment.deployment_id);
        } catch (err: any) {
            setRollbackError(getApiError(err, 'Rollback failed.'));
        } finally {
            setRollingBack(false);
        }
    };

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 1000,
                background: 'rgba(0,0,0,0.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '20px',
            }}
            onClick={onClose}
        >
            <div
                style={{ width: '100%', maxWidth: 560 }}
                onClick={e => e.stopPropagation()}
            >
            <GlassCard
                style={{ width: '100%', padding: 0, overflow: 'hidden' }}
            >
                {/* Header */}
                <div style={{
                    padding: '20px 24px 16px',
                    borderBottom: '1px solid var(--stroke)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Rocket size={20} style={{ color: 'var(--primary-500)' }} />
                        <span style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text)' }}>
                            Campaign Deployment
                        </span>
                        {deployment && (
                            <GlassBadge variant={overallStatusVariant(deployment.overall_status)}>
                                {overallStatusLabel(deployment.overall_status)}
                            </GlassBadge>
                        )}
                        {deploying && (
                            <GlassBadge variant="warning">Deploying...</GlassBadge>
                        )}
                    </div>
                    <GlassButton variant="ghost" onClick={onClose} style={{ padding: '4px 6px' }}>
                        <X size={18} />
                    </GlassButton>
                </div>

                <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '70vh', overflowY: 'auto' }}>

                    {/* Channel tiles */}
                    {deploying && !deployment && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                            <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                            Deploying campaign channels...
                        </div>
                    )}

                    {deployment && deployment.channels.length === 0 && (
                        <GlassCard style={{ padding: '16px', textAlign: 'center' }}>
                            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                                No connected channels found. Generate campaign assets and connect channels first.
                            </p>
                        </GlassCard>
                    )}

                    {deployment && deployment.channels.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {deployment.channels.map(ch => (
                                <div
                                    key={ch.name}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 12,
                                        padding: '12px 14px',
                                        background: 'var(--panel-strong)',
                                        border: '1px solid var(--stroke)',
                                        borderRadius: 'var(--radius-md)',
                                    }}
                                >
                                    <span style={{ color: 'var(--text-secondary)', flexShrink: 0 }}>
                                        {ch.status === 'not_connected'
                                            ? <WifiOff size={16} />
                                            : (CHANNEL_ICONS[ch.name] || <Wifi size={16} />)
                                        }
                                    </span>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text)' }}>
                                            {CHANNEL_LABELS[ch.name] || ch.name.replace(/_/g, ' ')}
                                        </div>
                                        {ch.note && (
                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                                                {ch.note}
                                            </div>
                                        )}
                                        {ch.error && (
                                            <div style={{ fontSize: '0.75rem', color: 'var(--danger)', marginTop: 2 }}>
                                                {ch.error}
                                            </div>
                                        )}
                                        {ch.url && (
                                            <a
                                                href={ch.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ fontSize: '0.75rem', color: 'var(--primary-500)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, textDecoration: 'none' }}
                                            >
                                                <ExternalLink size={11} /> View live
                                            </a>
                                        )}
                                    </div>
                                    <GlassBadge size="sm" variant={channelStatusVariant(ch.status)}>
                                        {ch.status.replace(/_/g, ' ')}
                                    </GlassBadge>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Rollback */}
                    {rollbackError && (
                        <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--danger)' }}>{rollbackError}</p>
                    )}
                    {canRollback && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <GlassButton
                                variant="danger"
                                onClick={handleRollback}
                                disabled={rollingBack}
                            >
                                <RotateCcw size={15} />
                                {rollingBack ? 'Rolling back...' : 'Rollback Deployment'}
                            </GlassButton>
                        </div>
                    )}

                    {/* Previous deployments */}
                    {previousDeployments.length > 1 && (
                        <>
                            <div style={{ borderTop: '1px solid var(--stroke)', paddingTop: 14 }}>
                                <h4 style={{ margin: '0 0 10px', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)' }}>
                                    Deployment History
                                </h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {previousDeployments.slice(0, 5).map(rec => (
                                        <div
                                            key={rec.deployment_id}
                                            style={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                padding: '8px 12px',
                                                background: 'var(--panel-strong)',
                                                border: '1px solid var(--stroke)',
                                                borderRadius: 'var(--radius-md)',
                                            }}
                                        >
                                            <div>
                                                <span style={{ fontSize: '0.8rem', color: 'var(--text)' }}>
                                                    {new Date(rec.deployed_at).toLocaleString()}
                                                </span>
                                                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginLeft: 8 }}>
                                                    {rec.channels.length} channel{rec.channels.length !== 1 ? 's' : ''}
                                                </span>
                                            </div>
                                            <GlassBadge size="sm" variant={overallStatusVariant(rec.overall_status)}>
                                                {rec.overall_status}
                                            </GlassBadge>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    padding: '12px 24px 16px',
                    borderTop: '1px solid var(--stroke)',
                    display: 'flex', justifyContent: 'flex-end',
                }}>
                    <GlassButton variant="secondary" onClick={onClose}>
                        Close
                    </GlassButton>
                </div>
            </GlassCard>
            </div>
        </div>
    );
};


// ---------------------------------------------------------------------------
// Auto-Scale Tab (extracted to avoid hooks-in-callback)
// ---------------------------------------------------------------------------

const AutoScaleTab: React.FC<{ campaign: Campaign; onRefresh: () => void }> = ({ campaign, onRefresh }) => {
    const [health, setHealth] = useState<any>(null);
    const [toggling, setToggling] = useState(false);
    const [iterating, setIterating] = useState(false);

    useEffect(() => {
        getCampaignHealth(campaign.campaign_id).then(setHealth).catch(console.error);
    }, [campaign.campaign_id]);

    const handleToggle = async () => {
        setToggling(true);
        try {
            if (campaign.auto_scale_enabled) {
                await disableAutoScale(campaign.campaign_id);
            } else {
                await enableAutoScale(campaign.campaign_id);
            }
            onRefresh();
        } catch (e) { console.error(e); }
        setToggling(false);
    };

    const handleIterate = async () => {
        setIterating(true);
        try {
            await iterateCampaign(campaign.campaign_id);
            onRefresh();
        } catch (e) { console.error(e); }
        setIterating(false);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <GlassCard style={{ padding: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div>
                        <h3 style={{ margin: 0, color: 'var(--text)' }}>
                            <Activity size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                            Autonomous Scaling
                        </h3>
                        <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                            Automatically test variants, promote winners, and generate iterations.
                        </p>
                    </div>
                    <GlassButton
                        variant={campaign.auto_scale_enabled ? 'danger' : 'primary'}
                        onClick={handleToggle}
                        disabled={toggling}
                    >
                        {campaign.auto_scale_enabled ? 'Disable' : 'Enable'} Auto-Scale
                    </GlassButton>
                </div>
                {health && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                        <div style={{ padding: 16, background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', textAlign: 'center' }}>
                            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: health.health_score >= 70 ? 'var(--success, #22c55e)' : health.health_score >= 40 ? 'var(--warning, #eab308)' : 'var(--danger, #dc2626)' }}>
                                {health.health_score}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Health Score</div>
                        </div>
                        <div style={{ padding: 16, background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', textAlign: 'center' }}>
                            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>{health.active_tests}</div>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Active Tests</div>
                        </div>
                        <div style={{ padding: 16, background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', textAlign: 'center' }}>
                            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>{health.total_assets}</div>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Total Assets</div>
                        </div>
                    </div>
                )}
                {health?.issues?.length > 0 && (
                    <div style={{ marginTop: 12, padding: 12, background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)' }}>
                        <p style={{ margin: '0 0 4px', fontWeight: 600, fontSize: '0.85rem', color: 'var(--text)' }}>Issues</p>
                        {health.issues.map((issue: string, i: number) => (
                            <p key={i} style={{ margin: '2px 0', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>• {issue}</p>
                        ))}
                    </div>
                )}
            </GlassCard>
            <GlassCard style={{ padding: 24 }}>
                <h3 style={{ margin: '0 0 12px', color: 'var(--text)' }}>
                    <TrendingUp size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                    Iterate Now
                </h3>
                <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                    Generate 2 new variants from the best-performing approved asset. Each iteration builds on what worked.
                </p>
                <GlassButton variant="primary" onClick={handleIterate} disabled={iterating}>
                    <Zap size={16} />
                    {iterating ? 'Generating iterations…' : 'Generate Iterations'}
                </GlassButton>
            </GlassCard>
        </div>
    );
};


// ---------------------------------------------------------------------------

const CampaignDetail: React.FC = () => {
    const { campaignId } = useParams<{ campaignId: string }>();
    const navigate = useNavigate();
    const [campaign, setCampaign] = useState<Campaign | null>(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [activeTab, setActiveTab] = useState('Overview');
    const [editingAsset, setEditingAsset] = useState<CampaignAsset | null>(null);
    const [activeSequenceId, setActiveSequenceId] = useState<string | null>(null);
    const [creatingSeq, setCreatingSeq] = useState(false);
    const [crmSyncing, setCrmSyncing] = useState(false);
    const [crmStatus, setCrmStatus] = useState<{ synced: boolean; crm_campaign_id?: string; last_synced?: string } | null>(null);
    const [crmError, setCrmError] = useState('');
    const [showDeployment, setShowDeployment] = useState(false);
    const [deploying, setDeploying] = useState(false);
    const [currentDeployment, setCurrentDeployment] = useState<DeploymentRecord | null>(null);
    const [deploymentHistory, setDeploymentHistory] = useState<DeploymentRecord[]>([]);

    // Brief editing state
    const [editingBrief, setEditingBrief] = useState(false);
    const [editingStrategy, setEditingStrategy] = useState(false);
    const [briefDraft, setBriefDraft] = useState<Partial<Campaign>>({});
    const [strategyDraft, setStrategyDraft] = useState('');
    const [savingBrief, setSavingBrief] = useState(false);
    const [savingStrategy, setSavingStrategy] = useState(false);

    // LP template for generation
    const [lpTemplateType, setLpTemplateType] = useState('lead_capture');

    // LP preview pipeline — same render-capture flow the LP module uses, so
    // the modal shows the brand-aware component-rendered page rather than the
    // legacy html_content snapshot served by /api/landing-pages/{id}/preview.
    const lpPreview = useLandingPagePreview();
    const [blogPostEditingAsset, setBlogPostEditingAsset] = useState<CampaignAsset | null>(null);

    // Per-asset regeneration state
    const [regenPickerId, setRegenPickerId] = useState<string | null>(null); // asset showing LP template picker
    const [regenTemplates, setRegenTemplates] = useState<Record<string, string>>({}); // per-asset selected LP template
    const [regenningId, setRegenningId] = useState<string | null>(null); // asset currently regenerating

    const [socialEditingAsset, setSocialEditingAsset] = useState<CampaignAsset | null>(null);
    const [overlayModalAsset, setOverlayModalAsset] = useState<CampaignAsset | null>(null);
    // Promote-winner confirmation: the asset to promote + sibling titles that will be archived
    const [promoteCandidate, setPromoteCandidate] = useState<{ asset: CampaignAsset; siblings: CampaignAsset[] } | null>(null);
    const [promoting, setPromoting] = useState(false);
    const [unlinkingGroup, setUnlinkingGroup] = useState<string | null>(null);

    // Products tab state
    const [linkedProducts, setLinkedProducts] = useState<Product[]>([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [showProductPicker, setShowProductPicker] = useState(false);
    const [unlinkingId, setUnlinkingId] = useState<string | null>(null);

    // Publish now state
    const [publishingAsset, setPublishingAsset] = useState<CampaignAsset | null>(null);
    const [publishPickerOpen, setPublishPickerOpen] = useState(false);
    const [publishAccounts, setPublishAccounts] = useState<any[]>([]);
    const [publishWpSites, setPublishWpSites] = useState<any[]>([]);
    const [publishSelectedId, setPublishSelectedId] = useState('');
    const [publishInProgress, setPublishInProgress] = useState<string | null>(null);
    const [publishResults, setPublishResults] = useState<Record<string, { success: boolean; post_url?: string; error?: string }>>({});

    const load = useCallback(async () => {
        if (!campaignId) return;
        try {
            setLoading(true);
            const data = await getCampaign(campaignId);
            setCampaign(data);
            // Fetch CRM sync status
            try {
                const status = await getCampaignCRMStatus(campaignId);
                setCrmStatus(status);
            } catch {
                // CRM status fetch is non-critical — silently ignore
            }
        } finally {
            setLoading(false);
        }
    }, [campaignId]);

    useEffect(() => { load(); }, [load]);

    const handleOpenPublishPicker = async (asset: CampaignAsset) => {
        setPublishingAsset(asset);
        setPublishSelectedId('');
        setPublishPickerOpen(true);

        if (asset.asset_type === 'blog_post') {
            try {
                const sites = await getPluginWordPressSites();
                setPublishWpSites(sites);
                setPublishAccounts([]);
            } catch { setPublishWpSites([]); }
        } else {
            const platform = asset.asset_type.replace('social_', '');
            try {
                const all = await getSocialAccounts(campaign?.project_id);
                const accounts = (all?.accounts || all || []).filter((a: any) => a.platform === platform && a.status === 'active');
                setPublishAccounts(accounts);
                setPublishWpSites([]);
            } catch { setPublishAccounts([]); }
        }
    };

    const handleConfirmPublish = async () => {
        if (!publishingAsset || !campaign || !publishSelectedId) return;
        setPublishPickerOpen(false);
        setPublishInProgress(publishingAsset.asset_id);

        const body: { social_account_id?: string; wordpress_site_id?: string } = {};
        if (publishingAsset.asset_type === 'blog_post') {
            body.wordpress_site_id = publishSelectedId;
        } else {
            body.social_account_id = publishSelectedId;
        }

        try {
            const result = await publishCampaignAssetNow(campaign.campaign_id, publishingAsset.asset_id, body);
            setPublishResults(prev => ({
                ...prev,
                [publishingAsset.asset_id]: {
                    success: result.success,
                    post_url: result.post_url,
                    error: result.error_message,
                },
            }));
            if (result.success) {
                setCampaign(prev => prev ? {
                    ...prev,
                    assets: prev.assets.map(a =>
                        a.asset_id === publishingAsset.asset_id
                            ? { ...a, status: 'published', external_post_url: result.post_url || null }
                            : a
                    ),
                } : prev);
            }
        } catch (err: any) {
            const msg = err?.response?.data?.detail || err?.message || 'Publish failed';
            setPublishResults(prev => ({
                ...prev,
                [publishingAsset.asset_id]: { success: false, error: msg },
            }));
        } finally {
            setPublishInProgress(null);
            setPublishingAsset(null);
        }
    };

    const handleGenerate = async (resume: boolean = false) => {
        if (!campaign) return;
        setGenerating(true);
        try {
            // POST /generate returns 202 immediately now; the modal owns
            // terminal-state detection + onComplete fires when generation
            // actually finishes. Don't setGenerating(false) here — that would
            // dismiss the modal mid-job.
            await generateCampaign(
                campaign.campaign_id,
                undefined, undefined, undefined,
                lpTemplateType,
                resume,
            );
        } catch {
            // Modal poller will pick up the failed status and surface buttons
        }
    };

    const handleRegenAsset = async (asset: CampaignAsset) => {
        if (!campaign) return;
        setRegenPickerId(null);
        setRegenningId(asset.asset_id);
        try {
            const lpTemplate = regenTemplates[asset.asset_id] || 'lead_capture';
            const updated = await regenerateSingleAsset(
                campaign.campaign_id,
                asset.asset_id,
                asset.asset_type === 'landing_page' ? lpTemplate : undefined,
            );
            setCampaign(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    assets: prev.assets.map(a => a.asset_id === updated.asset_id ? updated : a),
                };
            });
            if (asset.asset_type === 'landing_page' && updated.asset_ref_id) {
                navigate(`/landing-pages/${updated.asset_ref_id}/edit`);
            }
        } catch (e: any) {
            alert(getApiError(e, 'Regeneration failed'));
        } finally {
            setRegenningId(null);
        }
    };

    const handleDelete = async () => {
        if (!campaign || !window.confirm('Delete this campaign and all its assets?')) return;
        await deleteCampaign(campaign.campaign_id);
        navigate('/campaigns');
    };

    const startEditBrief = () => {
        if (!campaign) return;
        setBriefDraft({
            name: campaign.name,
            product_service: campaign.product_service || '',
            campaign_goal: campaign.campaign_goal,
            target_audience: campaign.target_audience || '',
            offer: campaign.offer || '',
            audience_segments: campaign.audience_segments || [],
            start_date: campaign.start_date ? campaign.start_date.slice(0, 10) : '',
            end_date: campaign.end_date ? campaign.end_date.slice(0, 10) : '',
            budget: campaign.budget ?? undefined,
            description: campaign.description || '',
            status: campaign.status,
        });
        setEditingBrief(true);
    };

    const cancelEditBrief = () => { setEditingBrief(false); setBriefDraft({}); };

    const saveBrief = async () => {
        if (!campaign) return;
        setSavingBrief(true);
        try {
            const payload: Record<string, any> = { ...briefDraft };
            if (payload.start_date === '') payload.start_date = null;
            if (payload.end_date === '') payload.end_date = null;
            if (payload.budget === '' || payload.budget === undefined) payload.budget = null;
            else payload.budget = Number(payload.budget);
            const updated = await updateCampaign(campaign.campaign_id, payload);
            setCampaign(updated);
            setEditingBrief(false);
        } finally {
            setSavingBrief(false);
        }
    };

    const startEditStrategy = () => {
        if (!campaign) return;
        setStrategyDraft(campaign.campaign_plan || '');
        setEditingStrategy(true);
    };

    const saveStrategy = async () => {
        if (!campaign) return;
        setSavingStrategy(true);
        try {
            const updated = await updateCampaign(campaign.campaign_id, { campaign_plan: strategyDraft });
            setCampaign(updated);
            setEditingStrategy(false);
        } finally {
            setSavingStrategy(false);
        }
    };

    // Load linked products when Products tab becomes active
    const loadLinkedProducts = useCallback(async () => {
        if (!campaignId) return;
        setProductsLoading(true);
        try {
            const data = await getCampaignProducts(campaignId);
            setLinkedProducts(Array.isArray(data) ? data : []);
        } catch {
            // non-critical
        } finally {
            setProductsLoading(false);
        }
    }, [campaignId]);

    useEffect(() => {
        if (activeTab === 'Products') {
            loadLinkedProducts();
        }
    }, [activeTab, loadLinkedProducts]);

    const handleLinkProduct = async (product: Product) => {
        if (!campaignId) return;
        await linkProductToCampaign(campaignId, product.product_id);
        setLinkedProducts(prev => [...prev, product]);
        setShowProductPicker(false);
    };

    const handleUnlinkProduct = async (productId: string) => {
        if (!campaignId) return;
        setUnlinkingId(productId);
        try {
            await unlinkProductFromCampaign(campaignId, productId);
            setLinkedProducts(prev => prev.filter(p => p.product_id !== productId));
        } finally {
            setUnlinkingId(null);
        }
    };

    const handleApproveAsset = async (asset: CampaignAsset) => {
        const newStatus = asset.status === 'approved' ? 'draft' : 'approved';
        const updated = await updateAsset(campaign!.campaign_id, asset.asset_id, { status: newStatus });
        setCampaign(prev => prev ? {
            ...prev,
            assets: prev.assets.map(a => a.asset_id === updated.asset_id ? updated : a)
        } : prev);
    };

    const handleUnarchiveAsset = async (asset: CampaignAsset) => {
        const updated = await updateAsset(campaign!.campaign_id, asset.asset_id, { status: 'draft' });
        setCampaign(prev => prev ? {
            ...prev,
            assets: prev.assets.map(a => a.asset_id === updated.asset_id ? updated : a)
        } : prev);
    };

    const handleConfirmPromote = async () => {
        if (!promoteCandidate || !campaign) return;
        setPromoting(true);
        try {
            await promoteVariant(campaign.campaign_id, promoteCandidate.asset.asset_id);
            setPromoteCandidate(null);
            await load();
        } catch (e) {
            console.error(e);
        } finally {
            setPromoting(false);
        }
    };

    const handleUnlinkGroup = async (variantGroup: string) => {
        if (!campaign) return;
        setUnlinkingGroup(variantGroup);
        try {
            await unlinkVariantGroup(campaign.campaign_id, variantGroup);
            await load();
        } catch (e) {
            console.error(e);
        } finally {
            setUnlinkingGroup(null);
        }
    };

    const handleDeleteAsset = async (asset: CampaignAsset) => {
        if (!window.confirm('Delete this asset?')) return;
        await deleteAsset(campaign!.campaign_id, asset.asset_id);
        setCampaign(prev => prev ? {
            ...prev,
            assets: prev.assets.filter(a => a.asset_id !== asset.asset_id)
        } : prev);
    };

    const handleSaveAsset = async (content: string, title: string, imageUrl?: string) => {
        const targetAsset = editingAsset || socialEditingAsset;
        if (!targetAsset || !campaign) return;
        const updated = await updateAsset(campaign.campaign_id, targetAsset.asset_id, { content, title });
        // If image changed, persist it too
        if (imageUrl !== undefined && imageUrl !== targetAsset.image_url) {
            await setCampaignAssetImageUrl(campaign.campaign_id, targetAsset.asset_id, imageUrl);
        }
        setCampaign(prev => prev ? {
            ...prev,
            assets: prev.assets.map(a =>
                a.asset_id === targetAsset.asset_id
                    ? { ...updated, image_url: imageUrl !== undefined ? imageUrl : updated.image_url }
                    : a
            )
        } : prev);
    };

    const handleCreateSequence = async () => {
        if (!campaign) return;
        setCreatingSeq(true);
        try {
            const seq = await createSequence(campaign.campaign_id, {
                project_id: campaign.project_id,
                name: `Email Sequence ${campaign.email_sequences.length + 1}`,
                trigger_type: 'manual',
            });
            setCampaign(prev => prev ? {
                ...prev,
                email_sequences: [...prev.email_sequences, seq]
            } : prev);
            setActiveSequenceId(seq.sequence_id);
        } finally {
            setCreatingSeq(false);
        }
    };

    const handleSyncToCRM = async () => {
        if (!campaign) return;
        setCrmSyncing(true);
        setCrmError('');
        try {
            const result = await syncCampaignToCRM(campaign.campaign_id);
            setCrmStatus({ synced: true, crm_campaign_id: result.crm_campaign_id, last_synced: new Date().toISOString() });
        } catch (err: any) {
            setCrmError(getApiError(err, 'Failed to sync campaign to SuiteCRM.'));
        } finally {
            setCrmSyncing(false);
        }
    };

    const loadDeploymentHistory = useCallback(async (cid: string) => {
        try {
            const history = await getCampaignDeployments(cid);
            setDeploymentHistory(history);
            if (history.length > 0) setCurrentDeployment(history[0]);
        } catch {
            // non-critical
        }
    }, []);

    const handleDeploy = async () => {
        if (!campaign) return;
        setDeploying(true);
        setShowDeployment(true);
        setCurrentDeployment(null);
        try {
            const record = await deployCampaign(campaign.campaign_id);
            setCurrentDeployment(record);
            // Refresh history
            await loadDeploymentHistory(campaign.campaign_id);
        } catch (err: any) {
            // Show error but keep modal open
            setCurrentDeployment({
                deployment_id: '',
                campaign_id: campaign.campaign_id,
                channels: [],
                overall_status: 'failed',
                deployed_at: new Date().toISOString(),
            });
        } finally {
            setDeploying(false);
        }
    };

    const handleRollback = async (deploymentId: string) => {
        await rollbackDeployment(deploymentId);
        if (campaign) {
            await loadDeploymentHistory(campaign.campaign_id);
        }
    };

    const handleOpenDeployment = async () => {
        if (!campaign) return;
        setShowDeployment(true);
        await loadDeploymentHistory(campaign.campaign_id);
    };

    if (loading) {
        return (
            <div className="campaigns-empty">
                <div className="generating-spinner" />
                <span>Loading campaign…</span>
            </div>
        );
    }

    if (!campaign) {
        return (
            <div className="campaigns-empty">
                <p>Campaign not found.</p>
                <GlassButton variant="secondary" onClick={() => navigate('/campaigns')}>
                    Back to Campaigns
                </GlassButton>
            </div>
        );
    }

    // If a sequence is open, show the builder
    if (activeSequenceId) {
        const seq = campaign.email_sequences.find(s => s.sequence_id === activeSequenceId);
        if (seq) {
            return (
                <EmailSequenceBuilder
                    sequence={seq}
                    campaign={campaign}
                    onBack={() => { setActiveSequenceId(null); load(); }}
                />
            );
        }
    }

    const canGenerate = campaign.generation_status === 'not_started' || campaign.generation_status === 'failed';
    const canRegenerate = campaign.generation_status === 'completed';

    return (
        <div className="campaign-detail">
            {/* Header */}
            <div className="campaign-detail-header">
                <div className="campaign-detail-title">
                    <GlassButton variant="ghost" onClick={() => navigate('/campaigns')}>
                        <ArrowLeft size={18} />
                    </GlassButton>
                    <h1>{campaign.name}</h1>
                    <GlassBadge variant={campaign.status === 'active' ? 'success' : campaign.status === 'paused' ? 'warning' : 'default'}>
                        {campaign.status}
                    </GlassBadge>
                    <span className={`goal-badge goal-${campaign.campaign_goal}`}>
                        {campaign.campaign_goal.replace('_', ' ')}
                    </span>
                </div>
                <div className="campaign-detail-actions">
                    {(canGenerate || canRegenerate) && (
                        <select
                            value={lpTemplateType}
                            onChange={e => setLpTemplateType(e.target.value)}
                            disabled={generating}
                            title="Landing page template"
                            style={{
                                background: 'var(--panel-strong)', border: '1px solid var(--stroke)',
                                borderRadius: 'var(--radius-md)', padding: '6px 10px',
                                color: 'var(--text)', fontSize: '0.82rem', cursor: 'pointer',
                            }}
                        >
                            <option value="lead_capture">LP: Lead Capture</option>
                            <option value="click_through">LP: Click-Through</option>
                            <option value="product">LP: Product</option>
                            <option value="webinar">LP: Webinar / Event</option>
                            <option value="event">LP: Event</option>
                            <option value="long_form_sales">LP: Long-Form Sales</option>
                            <option value="comparison">LP: Comparison</option>
                            <option value="squeeze">LP: Squeeze Page</option>
                        </select>
                    )}
                    {canGenerate && (
                        <GlassButton variant="primary" onClick={() => handleGenerate(false)} disabled={generating}>
                            <Zap size={16} />
                            {generating ? 'Generating…' : 'Generate All Assets'}
                        </GlassButton>
                    )}
                    {canRegenerate && (
                        <GlassButton variant="secondary" onClick={() => handleGenerate(false)} disabled={generating}>
                            <RefreshCw size={16} />
                            {generating ? 'Regenerating…' : 'Regenerate'}
                        </GlassButton>
                    )}
                    <GlassButton variant="primary" onClick={handleDeploy} disabled={deploying}>
                        <Rocket size={16} />
                        {deploying ? 'Deploying…' : 'Deploy Campaign'}
                    </GlassButton>
                    {deploymentHistory.length > 0 && !deploying && (
                        <GlassButton variant="secondary" onClick={handleOpenDeployment}>
                            <BarChart2 size={16} />
                            Deployments
                        </GlassButton>
                    )}
                    <GlassButton variant="danger" onClick={handleDelete}>
                        <Trash2 size={16} />
                    </GlassButton>
                </div>
            </div>

            {!generating && ['failed', 'cancelled', 'completed_with_gaps'].includes(campaign.generation_status) && (
                <GlassCard style={{ padding: '16px 20px', borderLeft: '3px solid var(--warning, #f59e0b)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                        <div>
                            <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                                {campaign.generation_status === 'completed_with_gaps'
                                    ? 'Generation completed with gaps'
                                    : campaign.generation_status === 'cancelled'
                                    ? 'Generation was cancelled'
                                    : 'Generation failed'}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                Click <strong>Retry</strong> to fill in missing variants — your existing assets are preserved.
                            </div>
                        </div>
                        <GlassButton variant="primary" onClick={() => handleGenerate(true)}>
                            <RotateCw size={15} />
                            Retry generation
                        </GlassButton>
                    </div>
                </GlassCard>
            )}

            {generating && (
                <GlassCard style={{ padding: '20px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center' }}>
                        <div className="generating-spinner" style={{ width: 28, height: 28, borderWidth: 3 }} />
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                            Generating assets… this may take 30–60 seconds.
                        </span>
                    </div>
                </GlassCard>
            )}

            {/* Tabs */}
            <GlassTabs
                tabs={TABS}
                activeTab={activeTab}
                onChange={setActiveTab}
            />

            {/* Overview */}
            {activeTab === 'Overview' && (() => {
                const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 10px', border: '1px solid var(--stroke)', borderRadius: '6px', fontSize: '0.85rem', background: 'var(--panel)', color: 'var(--text)', outline: 'none', boxSizing: 'border-box' };
                const labelStyle: React.CSSProperties = { display: 'block', marginBottom: '4px', fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 500 };
                const GOAL_OPTIONS = [
                    { value: 'awareness', label: 'Brand Awareness' },
                    { value: 'lead_gen', label: 'Lead Generation' },
                    { value: 'promotion', label: 'Promotion / Sales' },
                    { value: 'retention', label: 'Customer Retention' },
                ];
                const STATUS_OPTIONS = [
                    { value: 'draft', label: 'Draft' },
                    { value: 'active', label: 'Active' },
                    { value: 'paused', label: 'Paused' },
                ];
                return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {/* ── Brief card ── */}
                    <GlassCard style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: 'var(--text)' }}>Brief</h3>
                            {!editingBrief ? (
                                <GlassButton variant="secondary" onClick={startEditBrief} style={{ padding: '5px 12px', fontSize: '0.8rem' }}>
                                    <Edit2 size={14} /> Edit
                                </GlassButton>
                            ) : (
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <GlassButton variant="primary" onClick={saveBrief} disabled={savingBrief} style={{ padding: '5px 14px', fontSize: '0.8rem' }}>
                                        <Check size={14} /> {savingBrief ? 'Saving…' : 'Save'}
                                    </GlassButton>
                                    <GlassButton variant="secondary" onClick={cancelEditBrief} style={{ padding: '5px 12px', fontSize: '0.8rem' }}>
                                        <X size={14} /> Cancel
                                    </GlassButton>
                                </div>
                            )}
                        </div>
                        {editingBrief ? (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', fontSize: '0.85rem' }}>
                                <div>
                                    <label style={labelStyle}>Campaign Name</label>
                                    <input style={inputStyle} value={briefDraft.name ?? ''} onChange={e => setBriefDraft(d => ({ ...d, name: e.target.value }))} />
                                </div>
                                <div>
                                    <label style={labelStyle}>Status</label>
                                    <select style={inputStyle} value={briefDraft.status ?? 'draft'} onChange={e => setBriefDraft(d => ({ ...d, status: e.target.value }))}>
                                        {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={labelStyle}>Product / Service</label>
                                    <input style={inputStyle} value={(briefDraft as any).product_service ?? ''} onChange={e => setBriefDraft(d => ({ ...d, product_service: e.target.value }))} />
                                </div>
                                <div>
                                    <label style={labelStyle}>Goal</label>
                                    <select style={inputStyle} value={(briefDraft as any).campaign_goal ?? 'awareness'} onChange={e => setBriefDraft(d => ({ ...d, campaign_goal: e.target.value }))}>
                                        {GOAL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <label style={labelStyle}>Target Audience</label>
                                    <textarea style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} value={(briefDraft as any).target_audience ?? ''} onChange={e => setBriefDraft(d => ({ ...d, target_audience: e.target.value }))} />
                                </div>
                                <div>
                                    <label style={labelStyle}>Offer</label>
                                    <input
                                        style={inputStyle}
                                        placeholder="e.g. Fixed-fee will review"
                                        value={(briefDraft as any).offer ?? ''}
                                        onChange={e => setBriefDraft(d => ({ ...d, offer: e.target.value }))}
                                    />
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <EmailChipInput
                                        label="Audience Segments (optional)"
                                        placeholder="e.g. blended families — press Enter to add"
                                        value={((briefDraft as any).audience_segments ?? []) as string[]}
                                        onChange={(segs) => setBriefDraft(d => ({ ...d, audience_segments: segs }))}
                                        validator={(s) => s.trim().length > 0}
                                        errorMessage="Not a valid entry"
                                        helperText="Leave empty to let AI infer segments at generation time."
                                    />
                                </div>
                                <div>
                                    <label style={labelStyle}>Start Date</label>
                                    <input type="date" style={inputStyle} value={(briefDraft as any).start_date ?? ''} onChange={e => setBriefDraft(d => ({ ...d, start_date: e.target.value }))} />
                                </div>
                                <div>
                                    <label style={labelStyle}>End Date</label>
                                    <input type="date" style={inputStyle} value={(briefDraft as any).end_date ?? ''} onChange={e => setBriefDraft(d => ({ ...d, end_date: e.target.value }))} />
                                </div>
                                <div>
                                    <label style={labelStyle}>Budget ($)</label>
                                    <input type="number" style={inputStyle} min={0} step={100} value={(briefDraft as any).budget ?? ''} onChange={e => setBriefDraft(d => ({ ...d, budget: e.target.value === '' ? undefined : Number(e.target.value) }))} />
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <label style={labelStyle}>Description</label>
                                    <textarea style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} value={briefDraft.description ?? ''} onChange={e => setBriefDraft(d => ({ ...d, description: e.target.value }))} />
                                </div>
                            </div>
                        ) : (
                            <>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', fontSize: '0.85rem' }}>
                                    <div><span style={{ color: 'var(--text-secondary)' }}>Product/Service:</span><br /><strong>{campaign.product_service || '—'}</strong></div>
                                    <div><span style={{ color: 'var(--text-secondary)' }}>Goal:</span><br /><strong>{campaign.campaign_goal.replace('_', ' ')}</strong></div>
                                    <div><span style={{ color: 'var(--text-secondary)' }}>Audience:</span><br /><strong>{campaign.target_audience || '—'}</strong></div>
                                    <div><span style={{ color: 'var(--text-secondary)' }}>Offer:</span><br /><strong>{campaign.offer || '—'}</strong></div>
                                    <div><span style={{ color: 'var(--text-secondary)' }}>Segments:</span><br /><strong>{(campaign.audience_segments || []).join(', ') || '—'}</strong></div>
                                    <div><span style={{ color: 'var(--text-secondary)' }}>Dates:</span><br /><strong>
                                        {campaign.start_date ? new Date(campaign.start_date).toLocaleDateString() : '—'}
                                        {' — '}
                                        {campaign.end_date ? new Date(campaign.end_date).toLocaleDateString() : '—'}
                                    </strong></div>
                                    {campaign.budget != null && <div><span style={{ color: 'var(--text-secondary)' }}>Budget:</span><br /><strong>${campaign.budget.toLocaleString()}</strong></div>}
                                </div>
                                {campaign.description && (
                                    <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{campaign.description}</p>
                                )}
                            </>
                        )}
                    </GlassCard>

                    {/* ── Campaign Strategy card ── */}
                    {(campaign.campaign_plan || editingStrategy) && (
                        <GlassCard style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: 'var(--text)' }}>Campaign Strategy</h3>
                                {!editingStrategy ? (
                                    <GlassButton variant="secondary" onClick={startEditStrategy} style={{ padding: '5px 12px', fontSize: '0.8rem' }}>
                                        <Edit2 size={14} /> Edit
                                    </GlassButton>
                                ) : (
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <GlassButton variant="primary" onClick={saveStrategy} disabled={savingStrategy} style={{ padding: '5px 14px', fontSize: '0.8rem' }}>
                                            <Check size={14} /> {savingStrategy ? 'Saving…' : 'Save'}
                                        </GlassButton>
                                        <GlassButton variant="secondary" onClick={() => setEditingStrategy(false)} style={{ padding: '5px 12px', fontSize: '0.8rem' }}>
                                            <X size={14} /> Cancel
                                        </GlassButton>
                                    </div>
                                )}
                            </div>
                            {editingStrategy ? (
                                <textarea
                                    style={{ width: '100%', minHeight: '200px', padding: '12px', border: '1px solid var(--stroke)', borderRadius: '8px', fontSize: '0.85rem', lineHeight: 1.7, background: 'var(--panel)', color: 'var(--text)', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                                    value={strategyDraft}
                                    onChange={e => setStrategyDraft(e.target.value)}
                                />
                            ) : (
                                <p className="campaign-plan-content">{campaign.campaign_plan}</p>
                            )}
                        </GlassCard>
                    )}

                    {/* SuiteCRM sync card */}
                    <GlassCard style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: 'var(--text)' }}>SuiteCRM Sync</h3>
                                {crmStatus?.synced ? (
                                    <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                                        <CheckCircle size={13} style={{ verticalAlign: 'middle', marginRight: 4, color: 'var(--success, #16a34a)' }} />
                                        Synced · ID: {crmStatus.crm_campaign_id}
                                        {crmStatus.last_synced && ` · ${new Date(crmStatus.last_synced).toLocaleDateString()}`}
                                    </p>
                                ) : (
                                    <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                                        Not yet synced to SuiteCRM
                                    </p>
                                )}
                            </div>
                            <GlassButton
                                variant="secondary"
                                onClick={handleSyncToCRM}
                                disabled={crmSyncing}
                            >
                                <Share2 size={15} />
                                {crmSyncing ? 'Syncing…' : crmStatus?.synced ? 'Re-sync' : 'Sync to SuiteCRM'}
                            </GlassButton>
                        </div>
                        {crmError && (
                            <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--danger, #dc2626)' }}>{crmError}</p>
                        )}
                    </GlassCard>
                </div>
                );
            })()}

            {/* Assets */}
            {activeTab === 'Assets' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {campaign.assets.length === 0 ? (
                        <GlassCard style={{ padding: '32px', textAlign: 'center' }}>
                            <p style={{ color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                No assets generated yet.
                            </p>
                            {canGenerate && (
                                <GlassButton variant="primary" onClick={() => handleGenerate(false)} disabled={generating}>
                                    <Zap size={16} />
                                    Generate All Assets
                                </GlassButton>
                            )}
                        </GlassCard>
                    ) : (() => {
                        // Group assets: variant_group assets grouped together, others standalone
                        const groups: Record<string, CampaignAsset[]> = {};
                        const standalone: CampaignAsset[] = [];
                        campaign.assets.forEach(a => {
                            if (a.variant_group) {
                                if (!groups[a.variant_group]) groups[a.variant_group] = [];
                                groups[a.variant_group].push(a);
                            } else {
                                standalone.push(a);
                            }
                        });
                        // Sort variants within each group by index
                        Object.values(groups).forEach(g => g.sort((a, b) => (a.variant_index || 0) - (b.variant_index || 0)));

                        const renderAssetCard = (asset: CampaignAsset, showPromote?: boolean, groupSiblings?: CampaignAsset[]) => {
                            const meta = ASSET_TYPE_META[asset.asset_type] || { label: asset.asset_type, icon: <FileText size={14} /> };
                            const preview = (asset.content || '').replace(/<[^>]*>/g, '').slice(0, 200);
                            return (
                                <GlassCard key={asset.asset_id} className={`asset-card${asset.status === 'archived' ? ' archived' : ''}`}>
                                    <div className="asset-card-header">
                                        <span className="asset-card-type">
                                            {meta.icon} {meta.label}
                                            {asset.variant_label && (
                                                <span style={{ marginLeft: 6, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                                    ({asset.variant_label})
                                                </span>
                                            )}
                                        </span>
                                        <GlassBadge variant={ASSET_STATUS_VARIANT[asset.status] || 'default'}>
                                            {asset.status}
                                        </GlassBadge>
                                    </div>
                                    <p className="asset-card-title">{asset.title || meta.label}</p>
                                    <p className="asset-card-preview">{preview || 'No content yet.'}</p>
                                    {asset.image_url && (
                                        <img
                                            src={asset.image_url}
                                            alt="Social post image"
                                            style={{ width: '100%', height: '140px', objectFit: 'cover', borderRadius: 'var(--radius-md)', marginTop: 'var(--space-2)', border: '1px solid var(--stroke)', cursor: 'pointer' }}
                                            onClick={() => SOCIAL_ASSET_TYPES.includes(asset.asset_type) && setSocialEditingAsset(asset)}
                                        />
                                    )}
                                    <div className="asset-card-actions">
                                        {/* Row 1: primary content actions */}
                                        <div className="asset-card-actions-row">
                                            <GlassButton
                                                variant="secondary"
                                                onClick={() => {
                                                    if (asset.asset_type === 'landing_page' && asset.asset_ref_id) {
                                                        navigate(`/landing-pages/${asset.asset_ref_id}/edit`);
                                                    } else if (asset.asset_type === 'blog_post') {
                                                        setBlogPostEditingAsset(asset);
                                                    } else if (SOCIAL_ASSET_TYPES.includes(asset.asset_type)) {
                                                        setSocialEditingAsset(asset);
                                                    } else {
                                                        setEditingAsset(asset);
                                                    }
                                                }}
                                                style={{ flex: 1 }}
                                            >
                                                <Edit2 size={14} />
                                                Edit
                                            </GlassButton>
                                            {asset.asset_type === 'landing_page' && asset.asset_ref_id && (
                                                <GlassButton
                                                    variant="secondary"
                                                    onClick={() => lpPreview.open(asset.asset_ref_id!)}
                                                    disabled={lpPreview.isLoading}
                                                >
                                                    <Eye size={14} />
                                                </GlassButton>
                                            )}
                                            {asset.asset_type === 'ad_copy' && (
                                                <GlassButton
                                                    variant="secondary"
                                                    onClick={() => navigate('/ad-copy?tab=saved')}
                                                    title="View in Ad Copy module"
                                                >
                                                    <Megaphone size={14} />
                                                </GlassButton>
                                            )}
                                            {showPromote && asset.status === 'archived' && (
                                                <GlassButton
                                                    variant="secondary"
                                                    style={{ flex: 1 }}
                                                    onClick={() => handleUnarchiveAsset(asset)}
                                                    title="Restore this variant to draft"
                                                >
                                                    <RotateCcw size={14} />
                                                    Unarchive
                                                </GlassButton>
                                            )}
                                            {showPromote && asset.status !== 'archived' && (
                                                <GlassButton
                                                    variant="primary"
                                                    style={{ flex: 1 }}
                                                    onClick={() => {
                                                        const siblings = (groupSiblings || []).filter(s => s.asset_id !== asset.asset_id && s.status !== 'archived');
                                                        setPromoteCandidate({ asset, siblings });
                                                    }}
                                                    title="Pick this variant as the winner; archives the others"
                                                >
                                                    <Trophy size={14} />
                                                    Promote
                                                </GlassButton>
                                            )}
                                            {!showPromote && (
                                                <GlassButton
                                                    variant={asset.status === 'approved' ? 'secondary' : 'primary'}
                                                    style={{ flex: 1 }}
                                                    onClick={() => handleApproveAsset(asset)}
                                                >
                                                    <Check size={14} />
                                                    {asset.status === 'approved' ? 'Unapprove' : 'Approve'}
                                                </GlassButton>
                                            )}
                                        </div>
                                        {/* Row 2: regenerate + delete */}
                                        <div className="asset-card-actions-row">
                                            {regenningId === asset.asset_id ? (
                                                <GlassButton variant="secondary" disabled style={{ flex: 1 }}>
                                                    <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
                                                    Regenerating…
                                                </GlassButton>
                                            ) : asset.asset_type === 'landing_page' ? (
                                                regenPickerId === asset.asset_id ? (
                                                    <GlassButton
                                                        variant="secondary"
                                                        style={{ flex: 1 }}
                                                        onClick={() => setRegenPickerId(null)}
                                                    >
                                                        <X size={14} />
                                                        Cancel
                                                    </GlassButton>
                                                ) : (
                                                    <GlassButton
                                                        variant="secondary"
                                                        style={{ flex: 1 }}
                                                        onClick={() => setRegenPickerId(asset.asset_id)}
                                                        disabled={!!regenningId}
                                                    >
                                                        <RefreshCw size={14} />
                                                        Regen LP
                                                    </GlassButton>
                                                )
                                            ) : (
                                                <GlassButton
                                                    variant="secondary"
                                                    style={{ flex: 1 }}
                                                    onClick={() => handleRegenAsset(asset)}
                                                    disabled={!!regenningId}
                                                >
                                                    <RefreshCw size={14} />
                                                    Regenerate
                                                </GlassButton>
                                            )}
                                            <GlassButton variant="danger" onClick={() => handleDeleteAsset(asset)}>
                                                <Trash2 size={14} />
                                            </GlassButton>
                                        </div>
                                        {/* Row 3: publish now — blog_post and social_* only, not archived */}
                                        {['blog_post', 'social_facebook', 'social_instagram', 'social_linkedin', 'social_twitter'].includes(asset.asset_type) && asset.status !== 'archived' && (
                                            <div className="asset-card-actions-row">
                                                {asset.status === 'published' ? (
                                                    asset.external_post_url ? (
                                                        <a
                                                            href={asset.external_post_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: 'var(--primary)', textDecoration: 'none', flex: 1 }}
                                                        >
                                                            <ExternalLink size={13} /> View live
                                                        </a>
                                                    ) : (
                                                        <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', flex: 1 }}>Published</span>
                                                    )
                                                ) : publishInProgress === asset.asset_id ? (
                                                    <GlassButton variant="secondary" disabled style={{ flex: 1 }}>
                                                        <Send size={14} style={{ animation: 'spin 1s linear infinite' }} />
                                                        Publishing…
                                                    </GlassButton>
                                                ) : (
                                                    <>
                                                        <GlassButton
                                                            variant="secondary"
                                                            style={{ flex: 1 }}
                                                            disabled={asset.generation_status !== 'ready'}
                                                            title={
                                                                asset.generation_status === 'generating' || asset.generation_status === 'pending'
                                                                    ? 'Content still generating'
                                                                    : asset.generation_status === 'failed'
                                                                    ? 'Generation failed — regenerate first'
                                                                    : undefined
                                                            }
                                                            onClick={() => asset.generation_status === 'ready' && handleOpenPublishPicker(asset)}
                                                        >
                                                            <Send size={14} />
                                                            Publish now
                                                        </GlassButton>
                                                        {publishResults[asset.asset_id] && !publishResults[asset.asset_id].success && (
                                                            <span style={{ fontSize: '0.75rem', color: 'var(--danger, #dc2626)', flex: 1 }}>
                                                                {publishResults[asset.asset_id].error}
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                        {/* LP template picker — shown when Regen LP clicked, inside card actions */}
                                        {regenPickerId === asset.asset_id && (
                                            <div style={{
                                                display: 'flex', flexDirection: 'column', gap: 6,
                                                marginTop: 2, padding: '10px 12px',
                                                background: 'var(--panel-strong)',
                                                border: '1px solid var(--stroke)',
                                                borderRadius: 'var(--radius-md)',
                                            }}>
                                                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                                    LP template:
                                                </span>
                                                <select
                                                    value={regenTemplates[asset.asset_id] || 'lead_capture'}
                                                    onChange={e => setRegenTemplates(prev => ({ ...prev, [asset.asset_id]: e.target.value }))}
                                                    style={{
                                                        width: '100%', background: 'var(--panel)', border: '1px solid var(--stroke)',
                                                        borderRadius: 'var(--radius-sm)', padding: '4px 8px',
                                                        color: 'var(--text)', fontSize: '0.82rem',
                                                    }}
                                                >
                                                    <option value="lead_capture">Lead Capture</option>
                                                    <option value="click_through">Click-Through</option>
                                                    <option value="product">Product</option>
                                                    <option value="webinar">Webinar / Event</option>
                                                    <option value="event">Event</option>
                                                    <option value="long_form_sales">Long-Form Sales</option>
                                                    <option value="comparison">Comparison</option>
                                                    <option value="squeeze">Squeeze Page</option>
                                                </select>
                                                <GlassButton
                                                    variant="primary"
                                                    size="sm"
                                                    style={{ width: '100%' }}
                                                    onClick={() => handleRegenAsset(asset)}
                                                >
                                                    <RefreshCw size={13} />
                                                    Regenerate
                                                </GlassButton>
                                            </div>
                                        )}
                                    </div>
                                </GlassCard>
                            );
                        };

                        return (
                            <>
                                {/* Variant groups */}
                                {Object.entries(groups).map(([groupId, variants]) => {
                                    const typeMeta = ASSET_TYPE_META[variants[0]?.asset_type] || { label: variants[0]?.asset_type, icon: <FileText size={14} /> };
                                    return (
                                        <div key={groupId} className="variant-group-section">
                                            <div className="variant-group-header" style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <Layers size={16} />
                                                    <span>{typeMeta.label} — {variants.length} variants (pick a winner with Promote)</span>
                                                </div>
                                                <GlassButton
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={() => handleUnlinkGroup(groupId)}
                                                    disabled={unlinkingGroup === groupId}
                                                    title="Dissolve this variant group so all assets ship as a distinct series instead of competing variants"
                                                >
                                                    <Unlink size={13} />
                                                    {unlinkingGroup === groupId ? 'Unlinking…' : 'Keep all as series'}
                                                </GlassButton>
                                            </div>
                                            <div className="assets-grid">
                                                {variants.map(a => renderAssetCard(a, true, variants))}
                                            </div>
                                        </div>
                                    );
                                })}
                                {/* Standalone (non-variant) assets */}
                                {standalone.length > 0 && (
                                    <div className="assets-grid">
                                        {standalone.map(a => renderAssetCard(a, false))}
                                    </div>
                                )}
                            </>
                        );
                    })()}
                </div>
            )}

            {/* Journey Builder */}
            {activeTab === 'Journey Builder' && (
                <JourneyCanvas
                    campaignId={campaign.campaign_id}
                    projectId={campaign.project_id}
                />
            )}

            {/* Products */}
            {activeTab === 'Products' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Info note */}
                    <GlassCard style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10, background: 'var(--primary-50)', borderColor: 'var(--primary-200)' }}>
                        <Package size={16} style={{ color: 'var(--primary-500)', flexShrink: 0 }} />
                        <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                            AI will use these products' names and prices when generating campaign content.
                        </span>
                    </GlassCard>

                    {/* Action row */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <GlassButton variant="primary" icon={<Plus size={16} />} onClick={() => setShowProductPicker(true)}>
                            Link a Product
                        </GlassButton>
                    </div>

                    {/* Loading */}
                    {productsLoading && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)', fontSize: '0.9rem', padding: '12px 0' }}>
                            <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                            Loading linked products…
                        </div>
                    )}

                    {/* Empty state */}
                    {!productsLoading && linkedProducts.length === 0 && (
                        <GlassCard style={{ padding: '32px', textAlign: 'center' }}>
                            <Package size={28} style={{ color: 'var(--text-muted)', margin: '0 auto 12px' }} />
                            <p style={{ color: 'var(--text-secondary)', margin: '0 0 16px', fontSize: '0.9rem' }}>
                                No products linked yet. Link a product so AI can reference it in campaign content.
                            </p>
                            <GlassButton variant="primary" icon={<Plus size={16} />} onClick={() => setShowProductPicker(true)}>
                                Link a Product
                            </GlassButton>
                        </GlassCard>
                    )}

                    {/* Linked products list */}
                    {!productsLoading && linkedProducts.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {linkedProducts.map(p => (
                                <GlassCard key={p.product_id} style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                                        <Package size={18} style={{ color: 'var(--primary-500)', flexShrink: 0 }} />
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                                            <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                                                {p.category && <span>{p.category} · </span>}
                                                {p.price != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(p.price) : ''}
                                            </div>
                                        </div>
                                    </div>
                                    <GlassButton
                                        variant="secondary"
                                        size="sm"
                                        icon={<Unlink size={14} />}
                                        disabled={unlinkingId === p.product_id}
                                        loading={unlinkingId === p.product_id}
                                        onClick={() => handleUnlinkProduct(p.product_id)}
                                    >
                                        Unlink
                                    </GlassButton>
                                </GlassCard>
                            ))}
                        </div>
                    )}

                    {/* Product picker modal */}
                    {showProductPicker && (
                        <ProductPickerModal
                            onSelect={handleLinkProduct}
                            onClose={() => setShowProductPicker(false)}
                            excludeIds={linkedProducts.map(p => p.product_id)}
                        />
                    )}
                </div>
            )}

            {/* Auto-Scale */}
            {activeTab === 'Auto-Scale' && campaign && (
                <AutoScaleTab campaign={campaign} onRefresh={load} />
            )}

            {/* Performance */}
            {activeTab === 'Performance' && (
                <CampaignPerformancePanel campaignId={campaignId!} />
            )}

            {/* Asset editor modal */}
            {editingAsset && (
                <AssetEditor
                    asset={editingAsset}
                    onSave={handleSaveAsset}
                    onClose={() => setEditingAsset(null)}
                    onOpenOverlay={a => { setEditingAsset(null); setOverlayModalAsset(a); }}
                />
            )}

            {socialEditingAsset && campaign && (
                <SocialAssetEditor
                    platform={socialEditingAsset.asset_type}
                    variantLabel={socialEditingAsset.variant_label || undefined}
                    initialContent={socialEditingAsset.content || ''}
                    initialTitle={socialEditingAsset.title || ''}
                    initialImageUrl={socialEditingAsset.image_url || ''}
                    initialImageConcept={socialEditingAsset.image_concept || undefined}
                    onSave={async ({ content, title, imageUrl }) => {
                        await handleSaveAsset(
                            typeof content === 'object' ? JSON.stringify(content) : content,
                            title || '',
                            imageUrl,
                        );
                    }}
                    onClose={() => setSocialEditingAsset(null)}
                    fetchPexels={q =>
                        fetchCampaignAssetImage(campaign.campaign_id, socialEditingAsset.asset_id, q)
                    }
                    generateAI={async prompt => {
                        const r = await generateAdImage({
                            visual_concept: prompt,
                            platform: socialEditingAsset.platform || 'instagram',
                        });
                        // Persist the prompt as image_concept (IMPR-003)
                        try {
                            await setCampaignAssetImageUrl(
                                campaign.campaign_id,
                                socialEditingAsset.asset_id,
                                r.image_url,
                                prompt,
                            );
                        } catch { /* non-fatal */ }
                        return r;
                    }}
                    uploadImage={f =>
                        uploadCampaignAssetImage(campaign.campaign_id, socialEditingAsset.asset_id, f)
                    }
                    overlay={{
                        onOpen: () => {
                            // Capture the asset ref before setSocialEditingAsset(null) clears it.
                            // SocialAssetEditor calls onClose() after this, closing the editor.
                            const a = socialEditingAsset;
                            setSocialEditingAsset(null);
                            setOverlayModalAsset(a);
                        },
                    }}
                />
            )}

            {/* Text Overlay Modal */}
            {overlayModalAsset && overlayModalAsset.image_url && campaign && (
                <TextOverlayModal
                    baseImageUrl={
                        // On re-edit, use the stored original base (pre-text) image so we
                        // always composite against the clean original, not the composited output.
                        (overlayModalAsset.overlay_state as OverlayState | undefined)?.base_image_url ||
                        overlayModalAsset.image_url
                    }
                    initialOverlayState={overlayModalAsset.overlay_state || null}
                    fallbackVisualPrompt={overlayModalAsset.image_concept || undefined}
                    projectId={campaign.project_id ?? ''}
                    onClose={() => setOverlayModalAsset(null)}
                    onApplied={async ({ url, overlay_state }: { url: string; overlay_state: OverlayState }) => {
                        // Snapshot current campaign state for rollback on PATCH failure.
                        const previousCampaign = campaign;
                        // Optimistic update: reflect new image + overlay immediately.
                        setCampaign(prev => prev ? {
                            ...prev,
                            assets: prev.assets.map(a =>
                                a.asset_id === overlayModalAsset.asset_id
                                    ? { ...a, image_url: url, overlay_state }
                                    : a
                            ),
                        } : prev);
                        setOverlayModalAsset(null);
                        try {
                            await apiClient.patch(
                                `/campaigns/${campaign.campaign_id}/assets/${overlayModalAsset.asset_id}`,
                                { image_url: url, overlay_state },
                            );
                        } catch (e: any) {
                            // Revert optimistic update if persist failed.
                            setCampaign(previousCampaign);
                            alert(getApiError(e, 'Failed to save text overlay. Please try again.'));
                        }
                    }}
                />
            )}

            {/* Deployment dashboard modal */}
            {showDeployment && (
                <DeploymentDashboard
                    campaignId={campaignId!}
                    deployment={currentDeployment}
                    previousDeployments={deploymentHistory}
                    deploying={deploying}
                    onRollback={handleRollback}
                    onClose={() => setShowDeployment(false)}
                />
            )}

            {/* Promote-winner confirmation modal */}
            {promoteCandidate && (
                <div
                    style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                    onClick={() => !promoting && setPromoteCandidate(null)}
                >
                    <div style={{ width: '100%', maxWidth: 520 }} onClick={e => e.stopPropagation()}>
                        <GlassCard variant="strong" padding="none" style={{ overflow: 'hidden' }}>
                            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--stroke)', display: 'flex', alignItems: 'center', gap: 10 }}>
                                <Trophy size={18} style={{ color: 'var(--primary-500)' }} />
                                <span style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text)' }}>Promote variant?</span>
                            </div>
                            <div style={{ padding: '18px 24px', color: 'var(--text)' }}>
                                <p style={{ marginTop: 0 }}>
                                    Mark <strong>"{promoteCandidate.asset.title || promoteCandidate.asset.asset_type}"</strong> as the winning variant.
                                </p>
                                {promoteCandidate.siblings.length > 0 ? (
                                    <>
                                        <p style={{ marginBottom: 8, color: 'var(--text-secondary)' }}>
                                            The other {promoteCandidate.siblings.length} variant{promoteCandidate.siblings.length === 1 ? '' : 's'} in this group will be <strong>archived</strong>:
                                        </p>
                                        <ul style={{ margin: 0, paddingLeft: 20, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                                            {promoteCandidate.siblings.map(s => (
                                                <li key={s.asset_id}>{s.title || s.asset_type}</li>
                                            ))}
                                        </ul>
                                        <p style={{ marginTop: 12, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                                            Want to keep all of them as a series instead? Cancel and use <em>Keep all as series</em> on the group header.
                                        </p>
                                    </>
                                ) : (
                                    <p style={{ color: 'var(--text-secondary)' }}>No other active variants to archive.</p>
                                )}
                            </div>
                            <div style={{ padding: '14px 24px 18px', borderTop: '1px solid var(--stroke)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                                <GlassButton variant="secondary" onClick={() => setPromoteCandidate(null)} disabled={promoting}>
                                    Cancel
                                </GlassButton>
                                <GlassButton variant="primary" onClick={handleConfirmPromote} disabled={promoting}>
                                    <Trophy size={14} />
                                    {promoting ? 'Promoting…' : 'Promote & archive others'}
                                </GlassButton>
                            </div>
                        </GlassCard>
                    </div>
                </div>
            )}

            {/* Publish now picker modal */}
            {publishPickerOpen && publishingAsset && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1100,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <GlassCard style={{ width: 400, maxWidth: '90vw', padding: 24 }}>
                        <h3 style={{ margin: '0 0 16px', fontSize: '1rem', color: 'var(--text)' }}>
                            Publish now — {publishingAsset.title || publishingAsset.asset_type}
                        </h3>

                        {publishingAsset.asset_type === 'blog_post' ? (
                            publishWpSites.length === 0 ? (
                                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                    No plugin-connected WordPress sites found.{' '}
                                    <a href="/wordpress" style={{ color: 'var(--primary)' }}>Connect a site</a>
                                    {' '}(wpcom sites must use the Scheduling module).
                                </p>
                            ) : (
                                <>
                                    <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                                        WordPress site (plugin-connected only)
                                    </label>
                                    <select
                                        value={publishSelectedId}
                                        onChange={e => setPublishSelectedId(e.target.value)}
                                        style={{ width: '100%', background: 'var(--panel)', border: '1px solid var(--stroke)', borderRadius: 'var(--radius-sm)', padding: '6px 10px', color: 'var(--text)', fontSize: '0.85rem', marginBottom: 16 }}
                                    >
                                        <option value="">— select site —</option>
                                        {publishWpSites.map((s: any) => (
                                            <option key={s.site_id} value={s.site_id}>{s.site_url}</option>
                                        ))}
                                    </select>
                                </>
                            )
                        ) : (
                            publishAccounts.length === 0 ? (
                                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                    No active {publishingAsset.asset_type.replace('social_', '')} accounts found.{' '}
                                    <a href="/social-accounts" style={{ color: 'var(--primary)' }}>Connect an account</a>
                                </p>
                            ) : (
                                <>
                                    <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                                        {publishingAsset.asset_type.replace('social_', '').replace(/^\w/, c => c.toUpperCase())} account
                                    </label>
                                    <select
                                        value={publishSelectedId}
                                        onChange={e => setPublishSelectedId(e.target.value)}
                                        style={{ width: '100%', background: 'var(--panel)', border: '1px solid var(--stroke)', borderRadius: 'var(--radius-sm)', padding: '6px 10px', color: 'var(--text)', fontSize: '0.85rem', marginBottom: 16 }}
                                    >
                                        <option value="">— select account —</option>
                                        {publishAccounts.map((a: any) => (
                                            <option key={a.account_id} value={a.account_id}>
                                                {a.display_name || a.username || a.account_id}
                                            </option>
                                        ))}
                                    </select>
                                </>
                            )
                        )}

                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                            <GlassButton variant="secondary" onClick={() => { setPublishPickerOpen(false); setPublishingAsset(null); }}>
                                Cancel
                            </GlassButton>
                            <GlassButton
                                variant="primary"
                                disabled={!publishSelectedId}
                                onClick={handleConfirmPublish}
                            >
                                <Send size={14} />
                                Publish now
                            </GlassButton>
                        </div>
                    </GlassCard>
                </div>
            )}

            {/* Off-screen capture mount for the LP preview pipeline */}
            <lpPreview.HiddenCapture />

            {/* Live progress overlay during the long-running generate POST */}
            {campaign && (
                <CampaignGenerationProgressModal
                    campaignId={campaign.campaign_id}
                    isOpen={generating}
                    onClose={() => setGenerating(false)}
                    onComplete={() => {
                        setGenerating(false);
                        load();  // refresh the detail view to show new assets
                    }}
                    onRetry={() => handleGenerate(true)}
                />
            )}

            {/* Landing page preview modal */}
            {lpPreview.previewUrl && (
                <div
                    style={{
                        position: 'fixed', inset: 0, zIndex: 1000,
                        background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: 'var(--space-4)',
                    }}
                    onClick={lpPreview.close}
                >
                    <div
                        style={{
                            background: 'var(--panel)', borderRadius: 'var(--radius-lg)',
                            border: '1px solid var(--stroke)', width: '100%', maxWidth: '900px',
                            height: '80vh', display: 'flex', flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--stroke)', flexShrink: 0 }}>
                            <span style={{ fontWeight: 'var(--font-semibold)', color: 'var(--text)', fontSize: 'var(--text-base)' }}>
                                Landing Page Preview{lpPreview.previewTitle ? ` — ${lpPreview.previewTitle}` : ''}
                            </span>
                            <button
                                onClick={lpPreview.close}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <iframe
                            src={lpPreview.previewUrl}
                            style={{ flex: 1, border: 'none', width: '100%' }}
                            title="Landing Page Preview"
                        />
                    </div>
                </div>
            )}

            {/* Blog post rich text editor modal */}
            {blogPostEditingAsset && campaign && (
                <BlogPostEditorModal
                    asset={blogPostEditingAsset}
                    campaignId={campaign.campaign_id}
                    onClose={() => setBlogPostEditingAsset(null)}
                    onSaved={(updated) => {
                        setCampaign(prev => prev ? {
                            ...prev,
                            assets: prev.assets.map(a => a.asset_id === updated.asset_id ? updated : a),
                        } : prev);
                        setBlogPostEditingAsset(null);
                    }}
                />
            )}

        </div>
    );
};

export default CampaignDetail;
